import { useState, useCallback } from 'react'
import type { OrgType } from '@/lib/public-trpc'
import { getUtmContentSlug, utmSlugToFormOrgType } from '@/lib/utm-utils'
import { trackFormPrefill } from '@/lib/analytics'

// Re-export types consumers already import from here
export type { OrgType }

// -----------------------------------------------------------------------------
// New service + budget model (2026-04-12 redesign)
// -----------------------------------------------------------------------------
// The form now collects a multi-select of services and a monthly-spend range,
// replacing the old single-select `needs` and MRR-tier `budget`. The old fields
// still exist on the backend Zod schema, so at submission time we MAP the new
// shape onto the old fields (see mapServicesToNeeds / mapBudgetRangeToBudget
// in IntakeWizard.tsx). This keeps the frontend redesign decoupled from the
// app server deploy pipeline.

export type ServiceId =
  | 'cms-platform' // the Website capability; selectable like any other
  | 'new-website-content-ready'
  | 'new-website-with-coaching'
  | 'branding-logo'
  | 'custom-design'
  | 'ongoing-updates'
  | 'email-marketing'
  | 'social-media'

export type BudgetRange = '20-50' | '50-150' | '150-300' | '300-600' | '600-plus' | 'unsure'

/**
 * The budget bands, defined once.
 *
 * BudgetStep rendered these as options and ScheduleStep kept a parallel
 * `BUDGET_LABELS` map with the same strings retyped, so a reworded band changed
 * in one place and silently disagreed in the other. Hints mirror the modular
 * model in @gjl/voice/pricing; the flat $500/mo Festival Platform tier these
 * bands were originally drawn around was removed 2026-07-29.
 *
 * (The third vocabulary in IntakeWizard.tsx — `under-50`, `$50-200`, ... — is
 * NOT drift. It is a deliberate adapter onto the app server's legacy `Budget`
 * type, so the frontend can be reshaped without a server deploy.)
 */
export const BUDGET_BANDS: { value: BudgetRange; label: string; hint: string }[] = [
  { value: '20-50', label: '$20 – $50/mo', hint: 'Self-serve: website, email' },
  { value: '50-150', label: '$50 – $150/mo', hint: 'Self-serve packages + add-ons, run it yourself' },
  { value: '150-300', label: '$150 – $300/mo', hint: 'The full festival stack, or one managed service run for you' },
  { value: '300-600', label: '$300 – $600/mo', hint: 'Managed bundle: website plus email, run for you' },
  { value: '600-plus', label: '$600+/mo', hint: 'Concierge: everything done for you' },
  { value: 'unsure', label: 'Not sure yet', hint: "Let's discuss" },
]

/** Label lookup, derived so it cannot drift from the list above. */
export const BUDGET_LABELS: Record<BudgetRange, string> = Object.fromEntries(
  BUDGET_BANDS.map((b) => [b.value, b.label])
) as Record<BudgetRange, string>

export interface IntakeFormData {
  name: string
  email: string
  organization: string
  website: string
  orgType: OrgType | undefined
  services: ServiceId[]
  budgetRange: BudgetRange | undefined
  subscribeNewsletter: boolean
}

export interface SubmissionResult {
  calendlyUrl?: string
  selfServeUrl?: string
}

// -----------------------------------------------------------------------------
// 5-step flow (down from 9)
// -----------------------------------------------------------------------------
export type IntakeStep = 'contact' | 'organization' | 'services' | 'budget' | 'schedule'

const STEPS: IntakeStep[] = ['contact', 'organization', 'services', 'budget', 'schedule']

export function useIntakeWizard() {
  const [step, setStep] = useState<IntakeStep>('contact')
  const [direction, setDirection] = useState(1)
  const [formData, setFormData] = useState<IntakeFormData>(() => {
    // Pre-fill orgType from UTM params when arriving from outreach emails.
    // The welcome page forwards utm_content={orgSlug} to /get-started.
    const slug = getUtmContentSlug()
    const prefilledOrgType = slug ? utmSlugToFormOrgType(slug) : undefined
    if (prefilledOrgType && slug) {
      trackFormPrefill(slug)
    }
    return {
      name: '',
      email: '',
      organization: '',
      website: '',
      orgType: prefilledOrgType,
      // Website is no longer force-selected. Under the a-la-carte catalogue it is
      // one capability among several, not a floor: Applications and the Music
      // Library each stand alone. Pre-checking it meant every lead arrived asking
      // for a website whether they wanted one or not.
      services: [],
      budgetRange: undefined,
      subscribeNewsletter: true,
    }
  })
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null)

  const currentStepIndex = STEPS.indexOf(step)
  const totalSteps = STEPS.length
  const canGoBack = currentStepIndex > 0

  const updateField = useCallback(<K extends keyof IntakeFormData>(field: K, value: IntakeFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const goNext = useCallback(() => {
    const nextIndex = currentStepIndex + 1
    if (nextIndex < STEPS.length) {
      setDirection(1)
      setStep(STEPS[nextIndex])
    }
  }, [currentStepIndex])

  const goBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setDirection(-1)
      setStep(STEPS[prevIndex])
    }
  }, [currentStepIndex])

  const goToStep = useCallback(
    (targetStep: IntakeStep) => {
      const targetIndex = STEPS.indexOf(targetStep)
      setDirection(targetIndex > currentStepIndex ? 1 : -1)
      setStep(targetStep)
    },
    [currentStepIndex]
  )

  const completeSubmission = useCallback((result: SubmissionResult) => {
    setSubmissionResult(result)
    setDirection(1)
    setStep('schedule')
  }, [])

  return {
    step,
    direction,
    formData,
    updateField,
    goNext,
    goBack,
    goToStep,
    canGoBack,
    currentStep: currentStepIndex + 1,
    totalSteps,
    submissionResult,
    completeSubmission,
  }
}
